import { useEffect, useState } from 'react';
import type { StoryGroupDto } from '@tg-hookah/shared/dto';
import { useStories, useMarkStoryGroupViewed } from './useStories';
import { StoryViewer } from './StoryViewer';

export function StoriesRow() {
  const { data, isLoading } = useStories();
  const markViewed = useMarkStoryGroupViewed();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const groups: StoryGroupDto[] = (data ?? []).filter((group) => group.stories.length > 0);

  // Warm the cache with each group's first image while the device is idle, so
  // tapping a story opens instantly instead of waiting on a cold fetch.
  useEffect(() => {
    if (groups.length === 0) return;
    const idle =
      window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 300));
    const handle = idle(() => {
      for (const g of groups) {
        const first = g.stories[0];
        if (first && first.mediaType === 'IMAGE') {
          const img = new Image();
          img.src = first.mediaUrl;
        }
      }
    });
    return () => {
      if (window.cancelIdleCallback && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle as number);
      }
    };
    // Re-run only when the set of group ids changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.map((g) => g.id).join(',')]);

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto no-scrollbar py-2" aria-busy>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="w-16 h-16 rounded-full bg-surface animate-pulse shrink-0" />
        ))}
      </div>
    );
  }

  if (groups.length === 0) return null;

  const open = (idx: number, group: StoryGroupDto) => {
    setOpenIndex(idx);
    // Seeing the first content item marks the whole group viewed.
    markViewed.mutate(group.id);
  };

  return (
    <>
      <ul
        className="flex gap-3 overflow-x-auto no-scrollbar py-2 -mx-1 px-1"
        aria-label="stories"
      >
        {groups.map((g, idx) => (
          <li key={g.id} className="shrink-0">
            <button
              type="button"
              onClick={() => open(idx, g)}
              aria-label={g.title}
              data-track={`click:story-${g.id}`}
              className="flex flex-col items-center gap-1.5 w-[72px]"
            >
              <span
                className={`${g.isViewed ? 'story-ring-viewed' : 'story-ring-unread'} p-[1px] rounded-full`}
              >
                <span className="block bg-bg p-[2px] rounded-full">
                  <img
                    src={g.coverUrl}
                    alt=""
                    decoding="async"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                </span>
              </span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-white/55 truncate w-full text-center">
                {g.title}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {openIndex !== null ? (
        <StoryViewer
          groups={groups}
          startIndex={openIndex}
          onGroupViewed={(id) => markViewed.mutate(id)}
          onClose={() => setOpenIndex(null)}
        />
      ) : null}
    </>
  );
}
